import { useState } from 'react';
import { useQuery } from '@apollo/client/react';
import { useNavigate } from 'react-router-dom';
import { useFeatureFlags, FeatureFlagKeys } from '@modules/FeatureFlagModule';
import { mainLayoutLocators } from '@locators/main-layout';
import paths from '@app/routes/paths';
import { permissionSets } from '@app/permissions/permissions';
import usePermissions from '@app/permissions/usePermissions';
import { GET_DEFAULT_CALLER_ID_CONFIGURED } from '../queries/defaultCallerIdQueries';
import { strings } from '../strings';

export type DefaultCallerIdAnnouncementState = {
  shouldShow: boolean;
  title: string;
  testId: string;
  actionName?: string;
  onActionClick?: () => void;
  onClose: () => void;
};

const useDefaultCallerIdAnnouncement = (): DefaultCallerIdAnnouncementState => {
  const {
    [FeatureFlagKeys.featureDefaultCallerIdBanner]: bannerEnabled,
    [FeatureFlagKeys.featureConfigureOutboundSettings]:
      configureOutboundSettingsEnabled,
  } = useFeatureFlags();

  const [dismissed, setDismissed] = useState(false);
  const navigate = useNavigate();

  const hasPhoneSettingsPermission = usePermissions(
    permissionSets.admin.PHONE_SETTINGS,
    'any',
  );
  const canVisitOutboundNumbersPage =
    !!configureOutboundSettingsEnabled && hasPhoneSettingsPermission;

  const { data, error } = useQuery(GET_DEFAULT_CALLER_ID_CONFIGURED, {
    skip: !bannerEnabled,
    fetchPolicy: 'network-only',
  });

  if (error) {
    console.error('Default caller ID configured query failed:', error);
  }

  const shouldShow =
    !dismissed &&
    !!bannerEnabled &&
    !error &&
    data?.defaultCallerIdConfigured === false;

  return {
    shouldShow,
    title: strings.defaultCallerIdNotConfigured,
    testId: mainLayoutLocators.announcements.defaultCallerIdNotConfigured,
    ...(canVisitOutboundNumbersPage && {
      actionName: strings.defaultCallerIdNotConfiguredAction,
      onActionClick: () => navigate(`/${paths.outboundNumbers}`),
    }),
    onClose: () => setDismissed(true),
  };
};

export default useDefaultCallerIdAnnouncement;
